import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import * as d3 from 'd3';
import { OrgChart } from 'd3-org-chart';
import type { ZoomTransform } from 'd3-zoom';

import type { Service } from 'webapp-types';

import didInsert from 'webapp/modifiers/did-insert';
import onWindowEvents from 'webapp/modifiers/on-window-events';
import willDestroy from 'webapp/modifiers/will-destroy';
import FilterLineComponent from 'webapp/components/filter/line';
import GoalsTreeItemComponent from 'webapp/components/goals/tree/item';
import GoalsTreeZoomComponent from 'webapp/components/goals/tree/zoom';

import type { Goal } from 'webapp/models-custom';
import FilterSortGoalOKRsInGrid from 'webapp/models-custom/filter-sort/goal-okrs-in-grid';

import type { TreeItemData } from 'webapp/services/model-manager/goals';

interface Args {
	openView: (viewId: string) => void;
}

type ItemData = TreeItemData | { id: typeof TopStrategyItemId; parentId?: number; goal?: Goal };

export const TopStrategyItemId = -1;

type TreeNode = d3.HierarchyNode<ItemData>;

export default class GoalsTreeIndexComponent extends Component<Args> {
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;

	orgChart: OrgChart<ItemData> | null = null;

	renderedGoalsCt: HTMLElement | null = null;

	goalWrapperClass = 'goal-tree__item';

	filterSort: FilterSortGoalOKRsInGrid;

	goalsData: ItemData[] = [];

	// we test for safari to show different layout (without positions etc)
	// this is a hack to avoid this extremely old Safari bug https://bugs.webkit.org/show_bug.cgi?id=23113
	isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

	@tracked currentZoom = 1;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const filterSort = new FilterSortGoalOKRsInGrid({
			getAllItems: () => {
				return this.allGoals;
			},
			afterFilter: () => {
				this.setGoalsData();
				this.orgChart?.data(this.goalsData).render();
			},
			onOpenView: (viewId: string) => {
				this.args.openView(viewId);
			},
		});
		this.filterSort = filterSort;

		this.setGoalsData();
	}

	@cached
	get allGoals() {
		return this.mmGoalsService.goalModels.filter((goal) => goal.isPublic);
	}

	get strategyText() {
		return '<h4>Strategy</h4>' + this.mmGoalsService.companyStrategy;
	}

	@action
	setupGoalsRendered(node: HTMLElement) {
		this.renderedGoalsCt = node;
	}

	@action
	onTreeItemChange(goalId: number, node: HTMLElement) {
		// console.log('onTreeItemChange', goalId, node);

		// d3-org-chart types its state as any; nodesWrapper is the d3 selection of the nodes group
		const nodesWrapper = this.orgChart?.getChartState().nodesWrapper as
			{ node(): SVGGElement } | undefined;
		const container = nodesWrapper?.node().querySelector<HTMLElement>(`.container-${goalId}`);

		if (container) {
			this.updateTreeNodeFromRenderedNode(container, node);
		}
	}

	@action
	onResize() {
		this.orgChart?.render();
	}

	@action
	changeZoom(zoomIn: boolean) {
		if (zoomIn) {
			this.orgChart?.zoomIn();
		} else {
			this.orgChart?.zoomOut();
		}
	}

	@action
	setupView(_node: HTMLElement) {
		this.initializeOrgChart(this.goalsData);
	}

	@action
	openGoalInsightsPage(e: PointerEvent) {
		const goalId = (e.target as HTMLElement).closest('.card')?.getAttribute('data-id');
		if (goalId) {
			const goal = this.mmGoalsService.findModel(goalId);
			if (goal) {
				this.mmGoalsService.openGoalInsightsPage(goal);
			}
		}
	}

	@action
	teardownView(_node: HTMLElement) {
		// no obvious way to teardown?
	}

	private setGoalsData() {
		const goalsData = Array.from(
			this.mmGoalsService.generateTreeDataForGoals(this.filterSort.results),
		) as ItemData[];
		goalsData.unshift({
			id: TopStrategyItemId,
		});
		this.goalsData = goalsData;
	}

	private initializeOrgChart(data: ItemData[]) {
		const that = this;

		const orgChart = new OrgChart()
			.compact(false)
			.svgHeight(window.innerHeight - 60) // 60 is top navi
			.nodeHeight((rawItem) => {
				const item = rawItem as TreeNode;
				// strategy and first line under strategy is higher
				return item.data.parentId === TopStrategyItemId ||
					item.data.id === TopStrategyItemId
					? 200
					: 164;
			})
			.nodeWidth((_item) => 360)
			.childrenMargin((_item) => 120)
			.buttonContent(({ node }) => {
				const childrenCount = (node.data as { _directSubordinates?: number })
					._directSubordinates; // set by d3-org-chart
				if (childrenCount) {
					const isOpen = !!node.children?.length;
					return `<a><ui-icon name="${isOpen ? 'ChevronUp' : 'ChevronDown'}" size="xs"></ui-icon>${isOpen ? '' : childrenCount}</a>`;
				} else {
					return '';
				}
			})
			.nodeContent(function (this: HTMLDivElement, rawItem, _i, _nodeList, _state) {
				const item = rawItem as TreeNode;
				// this is called on every "change" (e.g. collapse/expand) on the org chart
				// even for nodes that aren't visible
				// so, if node is totally empty, we add the empty container
				// that will get filled in nodeUpdate (if node will be actually visible)
				const containerClass = that.getContainerClass(item);
				const alreadyRendered = !!this.querySelector(
					`.${containerClass} .${that.goalWrapperClass}`,
				);
				// console.log('nodeContent', { item, i, nodeList, state, alreadyRendered });
				if (alreadyRendered) {
					return this.innerHTML;
				} else {
					const isFirstLine = item.data.parentId === TopStrategyItemId;
					return `<div class="goal-tree__item-container ${containerClass} ${isFirstLine ? 'first-line' : ''}"></div>`;
				}
			})
			.nodeUpdate(function (this: SVGGElement, rawItem, _i, _nodeList) {
				const item = rawItem as TreeNode;
				// this is called on every "change" (e.g. collapse/expand) on the org chart
				// but only for visible nodes
				const containerClass = that.getContainerClass(item);
				const htmlContainer = this.querySelector<HTMLElement>(`.${containerClass}`);
				// console.log('nodeUpdate', { item, i, nodeList, htmlContainer });
				if (htmlContainer) {
					const isTopStrategy = item.data.id === TopStrategyItemId;
					if (isTopStrategy) {
						htmlContainer.innerHTML = `<div class="card card--strategy"><div class="overflow-wrapper">${that.strategyText}</div></div>`;
					} else {
						const alreadyRendered = !!htmlContainer.querySelector(
							`.${that.goalWrapperClass}`,
						);
						if (!alreadyRendered) {
							const renderedCt = that.renderedGoalsCt;
							if (renderedCt) {
								const sourceGoalCt = renderedCt.querySelector<HTMLElement>(
									`.${that.goalWrapperClass}[data-id="${item.data.id}"]`,
								);
								if (sourceGoalCt) {
									that.updateTreeNodeFromRenderedNode(
										htmlContainer,
										sourceGoalCt,
									);
								}
							}
						}
						// always bind the event because we might have went through alreadyRendered condition in nodeContent so we are back on plain HTML again
						that.bindInsightsButtonEvent(htmlContainer);
					}

					// change expand / collapsebutton sizes to match design
					const buttonNodeGroup = this.querySelector('.node-button-g');
					if (buttonNodeGroup) {
						if (isTopStrategy) {
							buttonNodeGroup.setAttribute('visibility', 'hidden');
						} else {
							const props = {
								width: 56,
								height: 32,
								x: -28, // -20 with 40 width, but we have 56
								y: 2,
							};

							[
								buttonNodeGroup.querySelector('.node-button-rect'),
								buttonNodeGroup.querySelector('foreignObject'),
							].forEach((node) => {
								Object.keys(props).forEach((key) => {
									node?.setAttribute(
										key,
										String(props[key as keyof typeof props]),
									);
								});
							});
						}
					}
				}
			})
			.linkUpdate(function (this: SVGGElement, _item, _i, _nodeList) {
				// dashed lines for aligned goals, solid for proper subgoals
				d3.select(this)
					.attr('stroke', 'var(--border-color-1)')
					.attr('stroke-width', 2)
					.attr('stroke-dasharray', (rawItem) => {
						const item = rawItem as TreeNode;
						const parentGoal = item.data.goal?.parentGoal;
						const isDashed =
							!!parentGoal &&
							!parentGoal.areChildrenSubgoals &&
							parentGoal.numericId === item.data.parentId;
						return isDashed ? '2, 4' : 'unset';
					});
			})
			.container('#goals-tree')
			.data(data)
			.render();

		this.currentZoom = orgChart.lastTransform().k;

		// Override the zoomed method
		const originalZoomed = orgChart.zoomed.bind(orgChart);
		orgChart.zoomed = (event: { transform: ZoomTransform }, d) => {
			originalZoomed(event, d);
			this.currentZoom = event.transform.k;
		};

		this.orgChart = orgChart as OrgChart<ItemData>;
	}

	private updateTreeNodeFromRenderedNode(container: HTMLElement, fromNode: HTMLElement) {
		// console.log('updateTreeNodeFromRenderedNode', container, fromNode);
		const newInnerHtml = `<div class="${this.goalWrapperClass}">${fromNode.innerHTML}</div>`;
		if (container.innerHTML !== newInnerHtml) {
			container.innerHTML = newInnerHtml;

			// bind the event because we have plain html pasted just now
			this.bindInsightsButtonEvent(container);
		}
	}

	private bindInsightsButtonEvent(container: HTMLElement) {
		container
			.querySelector<HTMLButtonElement>('.overlay .button')
			?.addEventListener('click', this.openGoalInsightsPage);
	}

	private getContainerClass(item: TreeNode) {
		return `container-${item.data.id}`;
	}
	<template>
		<div id="goals-tree-wrapper">
			<FilterLineComponent @model={{this.filterSort}} />

			<div
				class="goals-rendered"
				data-results-length={{this.filterSort.results.length}}
				{{! just to keep results getter up to date }}
				{{didInsert this.setupGoalsRendered}}
			>
				{{#each this.allGoals as |goal|}}
					<div class={{this.goalWrapperClass}} data-id={{goal.id}}>
						<GoalsTreeItemComponent
							@model={{goal}}
							@onChange={{this.onTreeItemChange}}
						/>
					</div>
				{{/each}}
			</div>

			<div
				id="goals-tree"
				class={{if this.isSafari "no-positions"}}
				{{didInsert this.setupView}}
				{{onWindowEvents this.onResize "resize" 200}}
				{{willDestroy this.teardownView}}
			></div>

			<GoalsTreeZoomComponent
				@currentZoom={{this.currentZoom}}
				@onChange={{this.changeZoom}}
			/>

		</div>
	</template>
}
